import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../shared/services/api.service';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ErrorDirective } from '../../shared/directives/error.directive';
import Countries from '../../../../resources/countries.json';
import { KeyValuePipe, NgForOf } from '@angular/common';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { AccountData } from '../../models/account-data.model';
import { environment } from '../../../environments/environment';
import { HelpersService } from '../../shared/services/helpers.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { finalize, timer } from 'rxjs';

@Component({
  selector: 'app-register',
  imports: [ReactiveFormsModule, ErrorDirective, NgForOf, KeyValuePipe, NgxSpinnerComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit {
  accountForm!: FormGroup;
  registerAvailable = environment.registerAvailable;

  readonly countries = Countries;

  constructor(
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router,
    private keycloak: Keycloak,
    private fb: FormBuilder,
    private helpersService: HelpersService,
    private ls: LocalStorageService
  ) {}

  ngOnInit() {
    if (!this.registerAvailable) {
      this.router.navigate(['/']);
    } else if (this.keycloak.authenticated) {
      this.router.navigate(['/dashboard']);
    }
    this.buildAccountForm();
  }

  buildAccountForm(): void {
    this.accountForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      username: ['', Validators.required],
      agency: [{ value: '', disabled: true }, Validators.required],
      company_name: ['', Validators.required],
      address: ['', Validators.required],
      country_code: ['', Validators.required],
      postal_code: ['', Validators.required],
      phone: ['', Validators.required],
      iata_number: [''],
    });
  }

  onEmailBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const email = input.value;

    if (email.includes('@')) {
      const items = email.split('@');
      const username = items[0];

      if (items.length > 1) {
        const agencyItems = items[1].split('.');

        if (agencyItems.length > 0) {
          const agency = agencyItems[0];

          this.accountForm.patchValue({
            username: username
              .toLowerCase()
              .substr(0, 40)
              .replace(/[^a-z0-9_.-]+/gi, ''),
            agency: agency.toLowerCase().substr(0, 40),
          });

          return;
        }
      }
    }

    // Clear username and agency if invalid email
    this.accountForm.patchValue({
      username: '',
      agency: '',
    });
  }

  saveChanges(): void {
    this.spinner.show();
    const accountData: AccountData = this.accountForm.getRawValue();

    this.apiService
      .registerAgency(accountData)
      .pipe(
        finalize(() => {
          this.spinner.hide();
        })
      )
      .subscribe({
        next: res => {
          if (res.status === 'OK') {
            if (res.data) {
              this.ls.setItem('role', res.data?.role);
              this.ls.setBool('isConsumerOwner', res.data?.is_consumer_owner);
              this.ls.setItem('email', res.data?.email);
              this.ls.setBool('agencyStatus', res.data?.agency_status);
              this.toastr.success(res.message || 'Agency has been registered.', 'Success');
              timer(2000).subscribe(() => this.router.navigate(['/']));
            }
          } else {
            this.helpersService.handleValidationErrors(
              this.accountForm,
              res.error as { [key: string]: string }
            );
            this.toastr.error(
              res?.error?.summary || 'Please correct the highlighted fields.',
              'Validation Error'
            );
          }
        },
        error: err => {
          console.error('Agency registration error:', err);
          this.toastr.error('Failed to register agency', 'Error');
        },
      });
  }
}
